/**
 * Claiming a file, and seeing who else has.
 *
 * WHY A LOCK IS WORTH A BUTTON
 *   CAD files are opaque binaries; two people cannot edit a part in parallel
 *   and merge the results. The only honest coordination is to say beforehand
 *   who is working on what. This is that.
 *
 *   When somebody else holds the lock we do NOT offer a way to take it. An
 *   archive where a colleague can quietly seize your claim is one where a lock
 *   means nothing. We offer to ask them instead, which writes a comment that
 *   @-mentions them — so the ask is notified, and stays with the file.
 */
import { useState } from 'react'
import { Lock as LockIcon, LockOpen, Hand } from 'lucide-react'
import { lockOn, blockingLock, type ProjectId, type Path } from '@lattice/core'
import { useWorkspace, personOf, locksHeldNow } from '@/store/workspace'
import { api } from '@/store/api'
import { Button, Tooltip, Avatar, Badge, Spinner } from './ui/primitives'
import { formatRelative, formatExact } from '@/lib/format'
import { useMemo } from 'react'

export function LockControls({ projectId, path }: { projectId: ProjectId; path: string }) {
  const lockLog = useWorkspace(s => s.locks)
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const locks = useMemo(() => locksHeldNow(lockLog), [lockLog])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [asking, setAsking] = useState(false)
  const [asked, setAsked] = useState(false)

  const direct = lockOn(locks, projectId, path as Path)
  const blocking = direct ?? blockingLock(locks, projectId, path as Path)
  const mine = blocking?.userId === me?.id

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true); setError(undefined)
    try { await fn() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (!blocking) {
    return (
      <span className="flex items-center gap-1">
        <Tooltip label="Lock this file so nobody else can change it while you work">
          <Button
            variant="ghost" size="icon" aria-label="Lock this file" disabled={busy}
            onClick={() => void run(() => api.lock({ projectId, path }))}
          >
            {busy ? <Spinner size={13} /> : <LockOpen className="h-3.5 w-3.5" strokeWidth={1.75} />}
          </Button>
        </Tooltip>
        {error && <span className="text-[11px] text-[var(--deleted)]">{error}</span>}
      </span>
    )
  }

  const holder = personOf(roster, blocking.userId)
  const inherited = direct === undefined

  return (
    <span className="flex items-center gap-1.5">
      <Tooltip
        label={
          <span>
            <span className="font-medium">
              {mine ? 'You locked this' : `${holder.name} locked this`}
            </span>
            <span className="block text-[var(--fg-muted)]">{formatExact(blocking.at)}</span>
            {inherited && <span className="block text-[var(--fg-muted)]">via the folder {blocking.path}</span>}
            {blocking.note && <span className="block text-[var(--fg-muted)]">{blocking.note}</span>}
          </span>
        }
      >
        <span className="flex items-center gap-1">
          <Badge tone={mine ? 'accent' : 'modified'}>
            <LockIcon className="h-2.5 w-2.5" strokeWidth={2} />
            {mine ? 'yours' : 'locked'}
          </Badge>
          {!mine && <Avatar user={holder} size={16} />}
        </span>
      </Tooltip>

      {mine && !inherited && (
        <Tooltip label="Release this lock">
          <Button
            variant="ghost" size="icon" aria-label="Release this lock" disabled={busy}
            onClick={() => void run(() => api.releaseLock(blocking.id))}
          >
            {busy ? <Spinner size={13} /> : <LockOpen className="h-3.5 w-3.5" strokeWidth={1.75} />}
          </Button>
        </Tooltip>
      )}

      {!mine && (
        <Tooltip label={asked ? 'Asked' : `Ask ${holder.name.split(' ')[0]} to release it`}>
          <Button
            variant="ghost" size="sm" disabled={asking || asked}
            onClick={() => {
              setAsking(true)
              void api.requestUnlock(blocking.id)
                .then(() => setAsked(true))
                .catch(e => setError((e as Error).message))
                .finally(() => setAsking(false))
            }}
          >
            {asking ? <Spinner size={12} /> : <Hand className="h-3 w-3" strokeWidth={1.75} />}
            {asked ? 'Asked' : 'Request'}
          </Button>
        </Tooltip>
      )}

      {error && <span className="text-[11px] text-[var(--deleted)]">{error}</span>}
    </span>
  )
}

/** A small padlock for a file row, when something claims it. */
export function LockDot({ projectId, path }: { projectId: ProjectId; path: string }) {
  const lockLog = useWorkspace(s => s.locks)
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const locks = useMemo(() => locksHeldNow(lockLog), [lockLog])
  const lock = blockingLock(locks, projectId, path as Path)
  if (!lock) return null
  const holder = personOf(roster, lock.userId)
  const mine = lock.userId === me?.id
  return (
    <Tooltip label={`${mine ? 'You' : holder.name} locked this ${formatRelative(lock.at)}`}>
      <LockIcon
        className={`h-3 w-3 shrink-0 ${mine ? 'text-[var(--accent)]' : 'text-[var(--modified)]'}`}
        strokeWidth={2}
      />
    </Tooltip>
  )
}
